/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { DynamicImage } from '@/components/dynamic-image';
import { getResolvedProductImage } from '@/lib/product/resolved-image';
import type { EnrichedProductItem } from '@/lib/product/product-utils';
import { IMAGE_PRESETS } from '@/lib/image-presets';
import { type DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { cn } from '@/lib/utils';

interface KitGroupImageProps {
    productItem: EnrichedProductItem;
    title: string;
    /**
     * Responsive widths array passed to `<DynamicImage>`. Provide two entries — 1× and 2× the
     * CSS slot width — so the browser can pick the right DIS-resized source for the device's
     * pixel ratio (e.g. `[80, 160]` for an 80 px slot). Defaults to `IMAGE_PRESETS.CART_LINE.widths`
     * ([120, 160]) which matches the standard cart-row kit slot (`w-28 md:w-40`).
     */
    widths?: DynamicImageDimensions;
    containerClassName: string;
    imageClassName?: string;
}

/**
 * Shared kit hero image renderer used by mini-cart and cart kit rows.
 * Keeps image-group fallback selection and DIS URL generation in one place.
 */
export default function KitGroupImage({
    productItem,
    title,
    widths = IMAGE_PRESETS.CART_LINE.widths as DynamicImageDimensions,
    containerClassName,
    imageClassName = 'h-full w-full object-cover',
}: KitGroupImageProps) {
    const image = getResolvedProductImage(productItem);

    // Derive intrinsic size from the largest numeric entry for CLS prevention.
    const numericWidths = (Array.isArray(widths) ? widths : []).filter((w): w is number => typeof w === 'number');
    const intrinsicSize = numericWidths.length > 0 ? Math.max(...numericWidths) : 160;

    return (
        <div className={cn('bg-product-image', containerClassName)}>
            {image ? (
                <DynamicImage
                    src={image.link}
                    alt={image.alt || productItem?.productName || title}
                    widths={widths}
                    heights={widths}
                    loading="lazy"
                    className={imageClassName}
                    imageProps={{ width: intrinsicSize, height: intrinsicSize, decoding: 'async' }}
                />
            ) : null}
        </div>
    );
}
